import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import styles from './index.module.scss';

import EmptyList from '@/components/ui/EmptyList';
import { PrimaryButton } from '@/components/ui/kit/Button';
import Checkbox from '@/components/ui/kit/Checkbox';
import { FieldNumber, FieldText } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import ModalWrapper from '@/components/ui/ModalWrapper';
import MoreMenu from '@/components/ui/MoreMenu';
import ProcessingButton from '@/components/ui/ProcessingButton';
import ProgressBar from '@/components/ui/ProgressBar';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';
import useParamDocsGenerator, { ArduPilot, PX4 } from '@/hooks/useParamDocsGenerator';
import useRobotTelemetry from '@/hooks/useRobotTelemetry';
import { modalManager } from '@/libs/ModalContainer';
import { convertInt32ToFloat32 } from '@/utils/BinaryUtils';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { download } from '@/utils/FileDownloader';

const cx = classNames.bind(styles);

const FORM_TYPE = {
  UNDEFINED: 0,
  READ_ONLY: 1,
  FIELD: 2,
  SELECT: 3,
  CHECKBOX: 4,
};

const Modal = ({ robot }) => {
  const firmware = useMemo(() => {
    if (robot.model.maker === 'ArduPilot') {
      return ArduPilot;
    } else if (robot.model.maker === 'PX4') {
      return PX4;
    }
  }, []);

  const { paramDocs, generateParamDocs } = useParamDocsGenerator(firmware);
  const robotParams = useSelector((state) => state.robotParams[robot.id]);
  const autopilotVersion = useRobotTelemetry(robot.id, MESSAGE.AUTOPILOT_VERSION);
  const [keyword, setKeyword] = useState('');
  const [paramInfo, setParamInfo] = useState();
  const selectedParamId = useRef();
  const fieldRef = useRef();
  const requestTimer = useRef();
  const paramCommand = useParamCommand();
  const { getAddress } = useMavLinkAddress();

  // 파라미터 준비 중 여부
  const isLoading = useMemo(() => {
    return !paramDocs || !robotParams.count || Object.keys(robotParams.params).length < robotParams.count;
  }, [paramDocs, robotParams]);

  // 노출 파라미터 선별
  const filteredParamIds = useMemo(() => {
    // 파라미터 준비 중
    if (isLoading) return [];

    // paramDocs 내 존재하는 파라미터만 선별
    const filtered = Object.keys(robotParams.params).filter((paramId) => Object.hasOwn(paramDocs, paramId));

    let result;
    // 검색 키워드 미입력 시
    if (keyword.trim().length === 0) {
      result = filtered;
    } else {
      result = filtered.filter((paramId) => {
        const upperCase = keyword.trim().toUpperCase();
        return paramId.includes(upperCase);
      });
    }

    return result.sort();
  }, [isLoading, keyword]);

  const [major, minor, patch] = useMemo(() => {
    return [
      (autopilotVersion.flightSwVersion >> 24) & 0xff,
      (autopilotVersion.flightSwVersion >> 16) & 0xff,
      (autopilotVersion.flightSwVersion >> 8) & 0xff,
    ];
  }, [autopilotVersion.flightSwVersion]);

  useMountEffect(() => {
    const filename = `${major}.${minor}.json`;

    let path;
    if (firmware === ArduPilot) {
      path = `/assets/mavlink-params/Ardupilot/${robot.model.vehicle}/${filename}`;
    } else if (firmware === PX4) {
      path = `/assets/mavlink-params/PX4/${filename}`;
    }
    if (!path) return;

    fetch(path)
      .then((response) => response.json())
      .then((data) => {
        generateParamDocs(data);
        paramCommand.list(robot);
      });
  });

  // 누락된 파라미터 찾기 및 재요청
  useUpdateEffect(() => {
    // 모든 파라미터 수신된 경우
    if (robotParams.count === Object.keys(robotParams.params).length) return;

    // 3초 후 누락된 파라미터 재요청 타이머 설정
    requestTimer.current = setTimeout(() => {
      const receivedIndexes = new Set(Object.values(robotParams.params).map(({ index }) => index));
      const address = getAddress(robot.id);

      const paramsGroup = Array.from({ length: robotParams.count }, (_, index) => index)
        .filter((index) => !receivedIndexes.has(index))
        .map((index) => [...address, 0, index]); // multiple 적용

      paramCommand.read(robot, paramsGroup);
    }, 3000);

    return () => {
      if (requestTimer.current) {
        clearTimeout(requestTimer.current);
      }
    };
  }, [robotParams.params]);

  useUpdateEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.value = paramInfo.value;
    }
  }, [paramInfo]);

  const changeKeyword = (e) => {
    setKeyword(e.target.value);
  };

  const doSelect = (e) => {
    const paramId = e.target.dataset.id;
    selectedParamId.current = paramId;

    const paramDoc = paramDocs[paramId];
    const { value, type } = robotParams.params[paramId];
    setParamInfo({ ...paramDoc, value, type });
  };

  const handleChange = (e) => {
    setParamInfo({ ...paramInfo, value: e.target.value });
  };

  const handleBlur = (e) => {
    setParamInfo({ ...paramInfo, value: Number(e.target.value) });
  };

  const toggleCheck = (index) => {
    const bit = 1 << Number(index);
    const isBitOn = Boolean(paramInfo.value & bit);

    let paramValue = paramInfo.value;
    if (isBitOn) {
      paramValue &= ~bit;
    } else {
      paramValue |= bit;
    }

    setParamInfo({ ...paramInfo, value: paramValue });
  };

  const doSave = async () => {
    try {
      let paramValue = Number(paramInfo.value);
      // 6: 32-bit signed integer
      if (paramInfo.type === 6 && firmware === PX4) {
        // float 변환
        paramValue = convertInt32ToFloat32(Math.trunc(paramValue));
      }
      await executeMavLinkCommand(robot, paramCommand.set(paramInfo.id, paramValue, paramInfo.type));
      return true;
    } catch (error) {
      return false;
    }
  };

  const doExportAsCSV = () => {
    const [sysId, compId] = getAddress(robot.id);
    // CSV 헤더 생성
    const header = 'Vehicle-Id,Component-Id,Name,Value,Type';

    // 파라미터 라인 생성
    const paramLines = Object.entries(robotParams.params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([paramId, { value, type }]) => `${sysId},${compId},${paramId},${value},${type}`);

    const content = [header, ...paramLines].join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const filename = `${robot.id}_parameters.csv`;
    download(blob, filename);
  };

  const doExportForQGC = () => {
    const [sysId, compId] = getAddress(robot.id);

    const gitRevision = autopilotVersion.flightCustomVersion
      .slice(0, firmware === PX4 ? 8 : 4)
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('');

    // QGC 형식 헤더 생성
    const header = `# Onboard parameters for Vehicle ${sysId}
#
# Stack: ${robot.model.maker}
# Vehicle: ${robot.model.name}
# Version: ${major}.${minor}.${patch}
# Git Revision: ${gitRevision}
#
# Vehicle-Id Component-Id Name Value Type`;

    // 파라미터 라인 생성
    const paramLines = Object.entries(robotParams.params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([paramId, { value, type }]) => `${sysId}\t${compId}\t${paramId}\t${value}\t${type}`);

    const content = [header, ...paramLines].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const filename = `${robot.id}_parameters.param`;
    download(blob, filename);
  };

  const doExportForMP = () => {
    const content = Object.entries(robotParams.params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([paramId, { value }]) => `${paramId},${value}`)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const filename = `${robot.id}_parameters.param`;
    download(blob, filename);
  };

  const close = () => {
    modalManager.hide();
  };

  return (
    <ModalWrapper className={cx('container')}>
      <div className={cx('wrapper')}>
        {isLoading && (
          <div className={cx('loading')}>
            <ProgressBar current={Object.keys(robotParams.params).length} total={robotParams.count ?? 0} />
          </div>
        )}
        {!isLoading && (
          <div className={cx('content')}>
            <div className={cx('left')}>
              <div className={cx('header')}>
                <FieldText placeholder="ID" onChange={changeKeyword} />
                <MoreMenu
                  data={[
                    { name: 'Export as CSV', callback: doExportAsCSV },
                    { name: 'Export for QGC', callback: doExportForQGC },
                    { name: 'Export for MP', callback: doExportForMP },
                  ]}
                />
              </div>
              {filteredParamIds.length === 0 && (
                <div className={cx('empty')}>
                  <EmptyList />
                </div>
              )}
              {filteredParamIds.length > 0 && (
                <ul>
                  {filteredParamIds.map((paramId) => (
                    <li
                      key={paramId}
                      data-id={paramId}
                      className={cx({ selected: paramId === selectedParamId.current })}
                      onClick={doSelect}>
                      {paramId}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={cx('console')}>
              {!paramInfo && (
                <div className={cx('empty')}>
                  <EmptyList message="No item selected" />
                </div>
              )}
              {paramInfo && (
                <>
                  <div className={cx('id')}>{paramInfo.id}</div>
                  <div className={cx('info')}>
                    <div className={cx('name')}>{paramInfo.name}</div>
                    <div className={cx('desc')}>{paramInfo.description}</div>
                  </div>
                  <div className={cx('form')}>
                    {paramInfo.formType === FORM_TYPE.READ_ONLY && <div className={cx('title')}>Current Value :</div>}
                    {paramInfo.formType !== FORM_TYPE.READ_ONLY && <div className={cx('title')}>Change Value :</div>}
                    {paramInfo.formType === FORM_TYPE.READ_ONLY && (
                      <div className={cx('field')}>
                        <FieldNumber value={paramInfo.value} readOnly />
                        {paramInfo.units !== null && <div className={cx('unit')}>{paramInfo.units}</div>}
                      </div>
                    )}
                    {[FORM_TYPE.UNDEFINED, FORM_TYPE.FIELD].includes(paramInfo.formType) && (
                      <div className={cx('field')}>
                        <FieldNumber
                          ref={fieldRef}
                          min={paramInfo.min}
                          max={paramInfo.max}
                          defaultValue={paramInfo.value}
                          onBlur={handleBlur}
                        />
                        <div className={cx('unit')}>{paramInfo.units}</div>
                      </div>
                    )}
                    {paramInfo.formType === FORM_TYPE.UNDEFINED && (
                      <div className={cx('message')}>
                        This value hasn't been validated. Please make sure it's correct.
                      </div>
                    )}
                    {paramInfo.formType === FORM_TYPE.FIELD && (
                      <ul className={cx('guide')}>
                        {paramInfo.min !== null && <li>∙ Min : {commaNumber(paramInfo.min)}</li>}
                        {paramInfo.max !== null && <li>∙ Max : {commaNumber(paramInfo.max)}</li>}
                        {paramInfo.increment !== null && <li>∙ Increment : {commaNumber(paramInfo.increment)}</li>}
                      </ul>
                    )}
                    {paramInfo.formType === FORM_TYPE.SELECT && (
                      <Select value={paramInfo.value ?? ''} onChange={handleChange}>
                        {paramInfo.values.map(({ value, description }, index) => (
                          <option key={index} value={Number(value)}>
                            {description}
                          </option>
                        ))}
                      </Select>
                    )}
                    {paramInfo.formType === FORM_TYPE.CHECKBOX && (
                      <>
                        <div className={cx('field')}>
                          <FieldNumber value={paramInfo.value} readOnly />
                        </div>
                        <div className={cx('options')}>
                          {paramInfo.bitmask.map(({ index, description }) => {
                            const isChecked = Boolean(paramInfo.value & (1 << Number(index)));
                            return (
                              <div key={index} className={cx('option')}>
                                <Checkbox checked={isChecked} onClick={() => toggleCheck(index)} />
                                {description}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {paramInfo.formType !== FORM_TYPE.READ_ONLY && (
                      <>
                        <ProcessingButton text="Save" doProcess={doSave} className={cx('button')} />
                        <div className={cx('warn')}>
                          <span className={cx('accent')}>Warning</span>
                          <br />
                          Altering settings while the vehicle is in flight may result in instability or even loss of the
                          vehicle. Ensure you fully understand the changes you're making and carefully verify your
                          inputs before saving!
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <PrimaryButton text="Close" center onClick={close} />
    </ModalWrapper>
  );
};

export default Modal;
